// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import cloudSlash from "../../../../../assets/images/controllers/cloud_grey.svg";
import leftControllerGreen from "../../../../../assets/images/controllers/left_green.svg";
import leftControllerGrey from "../../../../../assets/images/controllers/left_grey.svg";
import leftControllerRed from "../../../../../assets/images/controllers/left_red.svg";
import rightControllerGreen from "../../../../../assets/images/controllers/right_green.svg";
import rightControllerGrey from "../../../../../assets/images/controllers/right_grey.svg";
import rightControllerRed from "../../../../../assets/images/controllers/right_red.svg";

// Utils
import { getLocalDate } from "src/utils/date/dateHelper";

// Hooks
import { useExamStatus } from "src/hooks/exams/useExamStatus";

// Types
import { DeviceType } from "src/services/devices/types";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type DeviceCardContentType = {
  device: DeviceType;
  isLinked: boolean;
  shouldDisableLiveUpdate: boolean;
};

const DeviceCardContent = ({
  device,
  isLinked,
  shouldDisableLiveUpdate,
}: DeviceCardContentType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const { getExamStatus } = useExamStatus();
  const examStatus = getExamStatus({ device });

  // Update the controller icon color to be visible on dark mode — the
  // source SVG is a near-black silhouette, so inverting it turns it white.
  const isDark = theme.palette.mode === "dark";
  const greyIconFilter = isDark ? "invert(1)" : "none";

  // Update the offline text to be visible on dark mode
  const offlineColor = isDark ? "#8B94A1" : "#5B6D7A70";

  // Text color for dark vs light mode
  const primaryTextColor = isDark ? "#FFF" : onSurface;
  const mutedTextColor = isDark ? offlineColor : "#5B6D7A";

  const linkedExamsPatient = useMemo(() => {
    const devicePatient = device?.associatedExam?.exam?.patient;
    return isLinked
      ? `${devicePatient?.firstName} ${devicePatient?.lastName}`
      : "";
  }, [device?.associatedExam?.exam?.patient, isLinked]);

  const convertedDeviceExpiryDate = useMemo(
    () =>
      device?.expiryDate
        ? getLocalDate(device?.expiryDate, clinicInfo?.timezone)
        : null,
    [device?.expiryDate, clinicInfo?.timezone]
  );

  const leftDisconnected = useMemo(
    () =>
      !device?.deviceStatus?.controllerBatteryL ||
      device?.deviceStatus?.controllerBatteryL === -1,
    [device?.deviceStatus?.controllerBatteryL]
  );

  const rightDisconnected = useMemo(
    () =>
      !device?.deviceStatus?.controllerBatteryR ||
      device?.deviceStatus?.controllerBatteryR === -1,
    [device?.deviceStatus?.controllerBatteryR]
  );

  return (
    <>
      <Grid display="flex" justifyContent="space-between">
        <Grid container item width="80px" display="flex" alignItems="center">
          <Tooltip
            title={`${t("Left")}: ${
              leftDisconnected || device?.deviceStatus?.stage?.stageType == null
                ? t("Offline")
                : `${device?.deviceStatus?.controllerBatteryL * 100}%`
            }`}
          >
            <Grid container item xs={6} display="flex" flexDirection="column">
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="center"
                alignItems={
                  !leftDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={cloudSlash}
                  alt="offline"
                  width="16px"
                  style={{
                    visibility:
                      leftDisconnected ||
                      device?.deviceStatus?.stage?.stageType == null
                        ? "visible"
                        : "hidden",
                    filter: greyIconFilter,
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="center"
                alignItems={
                  !leftDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={
                    leftDisconnected ||
                    device?.deviceStatus?.stage?.stageType == null
                      ? leftControllerGrey
                      : device?.deviceStatus?.controllerBatteryL <= 0.2
                        ? leftControllerRed
                        : leftControllerGreen
                  }
                  alt="leftController"
                  width="20px"
                  style={{
                    filter:
                      leftDisconnected ||
                      device?.deviceStatus?.stage?.stageType == null
                        ? greyIconFilter
                        : "none",
                  }}
                />
              </Grid>
            </Grid>
          </Tooltip>

          <Tooltip
            title={`${t("Right")}: ${
              rightDisconnected ||
              device?.deviceStatus?.stage?.stageType == null
                ? t("Offline")
                : `${device?.deviceStatus?.controllerBatteryR * 100}%`
            }`}
          >
            <Grid container item xs={6} display="flex" flexDirection="column">
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="center"
                alignItems={
                  !rightDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={cloudSlash}
                  alt="offline"
                  width="16px"
                  style={{
                    visibility:
                      rightDisconnected ||
                      device?.deviceStatus?.stage?.stageType == null
                        ? "visible"
                        : "hidden",
                    filter: greyIconFilter,
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="center"
                alignItems={
                  !rightDisconnected &&
                  device?.deviceStatus?.stage?.stageType != null
                    ? "flex-end"
                    : "center"
                }
              >
                <img
                  src={
                    rightDisconnected ||
                    device?.deviceStatus?.stage?.stageType == null
                      ? rightControllerGrey
                      : device?.deviceStatus?.controllerBatteryR <= 0.2
                        ? rightControllerRed
                        : rightControllerGreen
                  }
                  alt="rightController"
                  width="20px"
                  style={{
                    filter:
                      rightDisconnected ||
                      device?.deviceStatus?.stage?.stageType == null
                        ? greyIconFilter
                        : "none",
                  }}
                />
              </Grid>
            </Grid>
          </Tooltip>
        </Grid>

        {/* Patient Name and Device Status */}
        <Grid display="flex" flexDirection="column" textAlign="right">
          <Typography
            fontSize="14px"
            fontWeight={600}
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "150px",
              color: shouldDisableLiveUpdate
                ? mutedTextColor
                : primaryTextColor,
            }}
          >
            {isLinked ? linkedExamsPatient : ""}
          </Typography>
          <Typography
            fontSize="14px"
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "150px",
              color: shouldDisableLiveUpdate
                ? mutedTextColor
                : primaryTextColor,
            }}
          >
            {isLinked && examStatus?.status ? examStatus?.status : ""}
          </Typography>
        </Grid>
      </Grid>

      {/* Device expiry text and Online/Offline status */}
      <Grid
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
      >
        <Grid display="flex" flexDirection="column" justifyContent="center">
          <Typography fontSize="9px" color={mutedTextColor}>
            {`${t("Expiry Date")}: ${convertedDeviceExpiryDate ?? "----"}`}
          </Typography>
        </Grid>

        <Grid
          item
          display="flex"
          alignItems="center"
          minWidth="fit-content"
          gap="4px"
          color={
            device?.deviceStatus?.stage?.stageType != null
              ? "#22BE39"
              : offlineColor
          }
        >
          {device?.deviceStatus?.stage?.stageType != null ? (
            <Box display="flex" alignItems="center" gap="4px">
              <Box
                borderRadius="100%"
                height="10px"
                width="10px"
                sx={{ backgroundColor: "#22BE39" }}
              />
              <Typography fontSize="14px" fontWeight={600}>
                {t("Online")}
              </Typography>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" gap="4px">
              <Box
                borderRadius="100%"
                height="10px"
                width="10px"
                sx={{ backgroundColor: offlineColor }}
              />
              <Typography fontSize="14px" fontWeight={600}>
                {t("Offline")}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default DeviceCardContent;
